import * as React from 'react'
import { useNavigate } from 'react-router-dom'
import {
  ArrowRight,
  Building2,
  CalendarClock,
  Check,
  ClipboardCheck,
  Plus,
  Presentation,
  Radar,
  Search,
  Shield,
  Target,
  Users,
} from 'lucide-react'
import { sb } from '@/lib/supabase'
import { useAuth } from '@/auth/auth-provider'
import { useQuery } from '@/hooks/use-query'
import { formatDate } from '@/lib/format'
import type { Team } from '@/lib/types'
import { cn } from '@/lib/utils'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Separator } from '@/components/ui/separator'
import { TeamDialog } from '@/components/team-dialog'
import { EmptyState, ErrorNote, Loading, Page } from '@/components/page'

/** One team's roll-up, assembled client-side from a handful of flat reads. */
interface TeamStats {
  members: number
  invited: number
  activities: number
  activitiesDone: number
  evaluations: number
  lastEvaluation: string | null
  nextWorkshop: string | null
}

const EMPTY: TeamStats = {
  members: 0,
  invited: 0,
  activities: 0,
  activitiesDone: 0,
  evaluations: 0,
  lastEvaluation: null,
  nextWorkshop: null,
}

/**
 * Mission Control — the cross-team view, for Don't Panic Labs staff only.
 *
 * Every other page in the portal is scoped to one team. This is the one place
 * that stands outside that: it lists every engagement, starts new ones, and
 * switches the whole app into a different team.
 *
 * DPL staff already read every team (`teams_read` → `app.can_read_team` is true
 * for admins) and already have every team in `memberships`, so switching is a
 * local selection, not a permission change. Hiding the page from non-staff is a
 * UI courtesy; RLS is what actually keeps a partner out of another partner's
 * data.
 */
export function MissionControlPage() {
  const { isAdmin, currentTeam, setCurrentTeamId, refresh } = useAuth()
  const navigate = useNavigate()
  const [creating, setCreating] = React.useState(false)
  const [justCreated, setJustCreated] = React.useState<Team | null>(null)
  const [q, setQ] = React.useState('')

  const { data, loading, error, refetch } = useQuery(async () => {
    // Flat reads rather than per-team counts: this is a handful of small rows
    // per table at accelerator scale, and one round trip each beats N queries
    // per team. Only the columns the roll-up needs come back.
    const [teams, members, activities, evaluations, workshops] = await Promise.all([
      sb().from('teams').select('*').order('name'),
      sb().from('team_members').select('team_id, user_id, active'),
      sb().from('activities').select('team_id, status'),
      sb().from('evaluations').select('team_id, conducted_on'),
      sb().from('workshops').select('team_id, starts_at, status'),
    ])
    for (const r of [teams, members, activities, evaluations, workshops]) {
      if (r.error) throw r.error
    }

    const stats = new Map<string, TeamStats>()
    const of = (id: string) => {
      let s = stats.get(id)
      if (!s) stats.set(id, (s = { ...EMPTY }))
      return s
    }

    for (const m of (members.data ?? []) as Array<{ team_id: string; user_id: string | null; active: boolean }>) {
      const s = of(m.team_id)
      if (!m.user_id) s.invited++
      else if (m.active) s.members++
    }
    for (const a of (activities.data ?? []) as Array<{ team_id: string; status: string }>) {
      const s = of(a.team_id)
      s.activities++
      if (a.status === 'complete') s.activitiesDone++
    }
    for (const e of (evaluations.data ?? []) as Array<{ team_id: string; conducted_on: string }>) {
      const s = of(e.team_id)
      s.evaluations++
      if (!s.lastEvaluation || e.conducted_on > s.lastEvaluation) s.lastEvaluation = e.conducted_on
    }
    const now = new Date().toISOString()
    for (const w of (workshops.data ?? []) as Array<{ team_id: string; starts_at: string | null; status: string }>) {
      if (!w.starts_at || w.starts_at < now || w.status === 'cancelled') continue
      const s = of(w.team_id)
      if (!s.nextWorkshop || w.starts_at < s.nextWorkshop) s.nextWorkshop = w.starts_at
    }

    return { teams: (teams.data ?? []) as Team[], stats }
  }, [])

  /**
   * Switch the app into a team. The switcher, the sidebar, and every
   * team-scoped query read `currentTeam` from AuthProvider, so this single
   * call re-points the whole portal.
   */
  const switchTo = React.useCallback(
    (team: Team, go = true) => {
      setCurrentTeamId(team.id)
      if (go) navigate('/dashboard')
    },
    [setCurrentTeamId, navigate],
  )

  /**
   * A team created here isn't in AuthProvider's membership list yet, and
   * selecting an id it doesn't know about would be silently overwritten by its
   * fallback-to-first-team rule. So refresh first, then select.
   */
  async function onCreated(team: Team) {
    setCreating(false)
    await Promise.all([refetch(), refresh()])
    setCurrentTeamId(team.id)
    setJustCreated(team)
  }

  // Defensive: the route is already gated on isAdmin, but this page reads
  // across every team and shouldn't render its framing if that ever changes.
  if (!isAdmin) {
    return (
      <Page title="Mission Control">
        <EmptyState
          icon={Shield}
          title="Don’t Panic Labs staff only"
          description="Mission Control spans every partner team, so it’s limited to DPL accounts."
        />
      </Page>
    )
  }

  const teams = data?.teams ?? []
  const needle = q.trim().toLowerCase()
  const visible = needle
    ? teams.filter((t) =>
        [t.name, t.company ?? '', t.slug].some((v) => v.toLowerCase().includes(needle)),
      )
    : teams

  // Active first, then paused, then archived — an engagement that's running is
  // what you came here for.
  const RANK: Record<string, number> = { active: 0, paused: 1, archived: 2 }
  const ordered = [...visible].sort(
    (a, b) => (RANK[a.status] ?? 3) - (RANK[b.status] ?? 3) || a.name.localeCompare(b.name),
  )

  const totals = teams.reduce(
    (acc, t) => {
      const s = data?.stats.get(t.id) ?? EMPTY
      if (t.status === 'active') acc.active++
      acc.people += s.members
      acc.invited += s.invited
      if (s.members + s.invited === 0) acc.empty++
      return acc
    },
    { active: 0, people: 0, invited: 0, empty: 0 },
  )

  return (
    <Page
      title="Mission Control"
      description="Every partner team in one place. Start a new engagement, or switch into one."
      wide
      actions={
        <>
          {/* The catalog every team's goal picker reads from. Lives here rather
              than under Admin because it is curriculum, like the decks — not
              account plumbing. */}
          <Button variant="outline" onClick={() => navigate('/mission-control/goals')}>
            <Target /> Common goals
          </Button>
          <Button onClick={() => setCreating(true)}>
            <Plus /> New team
          </Button>
        </>
      }
    >
      {error && <ErrorNote error={error} />}

      {justCreated && (
        <Card className="mb-6 border-primary/30 bg-primary/5 shadow-none">
          <CardContent className="flex flex-wrap items-center justify-between gap-3 py-4">
            <div className="flex min-w-0 items-start gap-3 text-sm">
              <Check className="mt-0.5 h-4 w-4 shrink-0 text-primary" />
              <p>
                <span className="font-medium">{justCreated.name}</span> is ready and you&apos;re now
                in it. Invite people from Admin, or open the dashboard to set up metrics.
              </p>
            </div>
            <div className="flex shrink-0 items-center gap-2">
              <Button size="sm" variant="ghost" onClick={() => setJustCreated(null)}>
                Dismiss
              </Button>
              <Button size="sm" onClick={() => navigate('/dashboard')}>
                Open dashboard <ArrowRight />
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {loading && !data ? (
        <Loading />
      ) : teams.length === 0 ? (
        <EmptyState
          icon={Building2}
          title="No teams yet"
          description="Create a team for a partner engagement. Everything else in the portal hangs off it."
          action={
            <Button onClick={() => setCreating(true)}>
              <Plus /> Create the first team
            </Button>
          }
        />
      ) : (
        <>
          <div className="mb-5 flex flex-wrap items-center gap-x-5 gap-y-2">
            <Summary label="teams" value={teams.length} />
            <Summary label="active" value={totals.active} />
            <Summary label="people" value={totals.people} />
            {totals.invited > 0 && <Summary label="not signed in yet" value={totals.invited} muted />}
            {totals.empty > 0 && <Summary label="with nobody on them" value={totals.empty} muted />}
            <div className="flex-1" />
            <div className="relative w-full sm:w-64">
              <Search className="pointer-events-none absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
              <Input
                value={q}
                onChange={(e) => setQ(e.target.value)}
                placeholder="Find a team"
                className="pl-8"
                aria-label="Find a team"
              />
            </div>
          </div>

          {ordered.length === 0 ? (
            <EmptyState icon={Search} title={`Nothing matches “${q.trim()}”`} />
          ) : (
            <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
              {ordered.map((team) => (
                <TeamCard
                  key={team.id}
                  team={team}
                  stats={data?.stats.get(team.id) ?? EMPTY}
                  current={team.id === currentTeam?.id}
                  onSwitch={() => switchTo(team)}
                />
              ))}
            </div>
          )}
        </>
      )}

      {creating && (
        <TeamDialog onClose={() => setCreating(false)} onSaved={(t) => void onCreated(t)} />
      )}
    </Page>
  )
}

function Summary({ label, value, muted }: { label: string; value: number; muted?: boolean }) {
  return (
    <div className="flex items-baseline gap-1.5">
      <span className={cn('text-xl font-semibold tabular-nums', muted && 'text-muted-foreground')}>
        {value}
      </span>
      <span className="text-sm text-muted-foreground">{label}</span>
    </div>
  )
}

function TeamCard({
  team,
  stats,
  current,
  onSwitch,
}: {
  team: Team
  stats: TeamStats
  current: boolean
  onSwitch: () => void
}) {
  return (
    <Card className={cn('flex flex-col', current && 'border-primary/50 ring-1 ring-primary/20')}>
      <CardHeader className="pb-3">
        <div className="flex items-start justify-between gap-2">
          <CardTitle className="text-base">{team.name}</CardTitle>
          <div className="flex shrink-0 items-center gap-1.5">
            {current && (
              <Badge variant="default" className="gap-1 text-[10px]">
                <Radar className="h-3 w-3" /> Current
              </Badge>
            )}
            <Badge
              variant={team.status === 'active' ? 'success' : team.status === 'paused' ? 'warning' : 'muted'}
              className="text-[10px]"
            >
              {team.status}
            </Badge>
          </div>
        </div>
        <CardDescription>
          {[team.company, `/${team.slug}`, team.started_on ? `since ${formatDate(team.started_on)}` : null]
            .filter(Boolean)
            .join(' · ')}
        </CardDescription>
      </CardHeader>

      <CardContent className="flex flex-1 flex-col gap-3">
        <Separator />

        <dl className="grid grid-cols-3 gap-2 text-sm">
          <Stat icon={Users} label="members" value={stats.members} note={stats.invited ? `${stats.invited} invited` : null} />
          <Stat
            icon={Presentation}
            label="topics"
            value={stats.activities}
            note={stats.activities > 0 ? `${stats.activitiesDone} done` : null}
          />
          <Stat
            icon={ClipboardCheck}
            label="evaluations"
            value={stats.evaluations}
            note={stats.lastEvaluation ? formatDate(stats.lastEvaluation) : null}
          />
        </dl>

        {stats.nextWorkshop && (
          <p className="flex items-center gap-1.5 text-xs text-muted-foreground">
            <CalendarClock className="h-3.5 w-3.5 shrink-0" />
            Next workshop {formatDate(stats.nextWorkshop)}
          </p>
        )}

        {stats.members + stats.invited === 0 && (
          <p className="text-xs text-warning">Nobody on this team yet — invite people from Admin.</p>
        )}

        <div className="flex-1" />

        <Button
          variant={current ? 'outline' : 'default'}
          className="w-full"
          onClick={onSwitch}
        >
          {current ? 'Open dashboard' : `Switch to ${team.name}`}
          <ArrowRight />
        </Button>
      </CardContent>
    </Card>
  )
}

function Stat({
  icon: Icon,
  label,
  value,
  note,
}: {
  icon: React.ComponentType<{ className?: string }>
  label: string
  value: number
  note: string | null
}) {
  return (
    <div className="min-w-0">
      <dt className="flex items-center gap-1 text-[11px] text-muted-foreground">
        <Icon className="h-3.5 w-3.5 shrink-0" />
        <span className="truncate">{label}</span>
      </dt>
      <dd className="mt-0.5 font-semibold tabular-nums">
        {value}
        {note && <span className="ml-1.5 text-[11px] font-normal text-muted-foreground">{note}</span>}
      </dd>
    </div>
  )
}
